"use client";

import Image from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

const SESSION_KEY = "maxridge-welcome-seen";
const VISIBLE_MS = 2800;
const EXIT_MS = 700;

export function WelcomeSplash() {
  const reduced = useReducedMotion();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    } catch {
      /* ignore */
    }

    setShow(true);
    document.documentElement.classList.add("welcome-splash-active");

    const hideAt = reduced ? 900 : VISIBLE_MS;
    const timer = window.setTimeout(() => {
      setShow(false);
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
    }, hideAt);

    return () => {
      window.clearTimeout(timer);
      document.documentElement.classList.remove("welcome-splash-active");
    };
  }, [reduced]);

  useEffect(() => {
    if (show) return;
    document.documentElement.classList.remove("welcome-splash-active");
  }, [show]);

  return (
    <AnimatePresence>
      {show ? (
        <motion.div
          key="welcome-splash"
          className="welcome-splash fixed inset-0 z-[200] flex items-center justify-center overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            transition: { duration: EXIT_MS / 1000, ease: [0.22, 1, 0.36, 1] },
          }}
          role="presentation"
          aria-hidden
        >
          {/* Atmosphere */}
          <div className="absolute inset-0 bg-[#06101f]" />
          <motion.div
            className="absolute -left-[15%] top-[-20%] h-[55vmax] w-[55vmax] rounded-full bg-[radial-gradient(circle,rgba(0,96,255,0.45),transparent_68%)] blur-3xl"
            animate={reduced ? undefined : { x: [0, 40, -10, 0], y: [0, 20, -15, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute -right-[10%] bottom-[-25%] h-[50vmax] w-[50vmax] rounded-full bg-[radial-gradient(circle,rgba(0,184,200,0.32),transparent_70%)] blur-3xl"
            animate={reduced ? undefined : { x: [0, -30, 15, 0], y: [0, -25, 10, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute left-[45%] top-[20%] h-[28vmax] w-[28vmax] rounded-full bg-[radial-gradient(circle,rgba(255,106,43,0.2),transparent_68%)] blur-3xl"
            animate={reduced ? undefined : { scale: [1, 1.12, 1], opacity: [0.45, 0.75, 0.45] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Fine grid */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.35) 1px, transparent 1px)",
              backgroundSize: "56px 56px",
              maskImage: "radial-gradient(ellipse at center, black 20%, transparent 72%)",
            }}
          />

          <div className="relative z-10 px-6 text-center">
            <motion.p
              className="mb-6 text-[11px] font-semibold uppercase tracking-[0.34em] text-white/55"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.08 }}
            >
              Welcome
            </motion.p>

            <motion.div
              className="mx-auto flex justify-center"
              initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.75, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
            >
              <Image
                src="/logos/maxridge-logo-dark.png"
                alt="Maxridge"
                width={420}
                height={147}
                priority
                className="h-auto w-[min(78vw,26rem)] object-contain"
              />
            </motion.div>

            <motion.p
              className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-white/70 sm:text-base"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42 }}
            >
              Intelligence for the enterprise era.
            </motion.p>

            <motion.div
              className="mx-auto mt-9 h-px w-40 overflow-hidden rounded-full bg-white/15"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
            >
              <motion.div
                className="h-full origin-left bg-gradient-to-r from-[#0060ff] via-[#00b8c8] to-[#ff6a2b]"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: reduced ? 0.5 : 2.1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </motion.div>
          </div>

          {/* Exit wipe accent */}
          <motion.div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#f7f9fc]/25 to-transparent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 0.8 }}
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
